// Prêmios dos marcos de 7 e 30 dias de sequência (admin): um cartão e um form por marco.
import { useEffect, useState } from 'react';
import { Button, Card, Form, InputNumber, Typography, message } from 'antd';
import {
  listChestMilestones,
  updateChestMilestone,
  type ChestMilestone,
  type ChestMilestonePayload,
} from '../../services/admin';
import { apiErrorMessage } from './desafios';

const SAVE_ERROR = 'Falha ao salvar o marco.';
const EMPTY_ERROR = 'O marco precisa dar pelo menos um prêmio.';

type FormValues = Partial<Record<keyof ChestMilestonePayload, number | null>>;

// InputNumber limpo entrega null; a API espera número.
const toPayload = (v: FormValues): ChestMilestonePayload => ({
  passDays: v.passDays ?? 0,
  hintCards: v.hintCards ?? 0,
  streakProtectors: v.streakProtectors ?? 0,
  subscriberHintCards: v.subscriberHintCards ?? 0,
  subscriberStreakProtectors: v.subscriberStreakProtectors ?? 0,
});

function MilestoneCard({ milestone }: { milestone: ChestMilestone }): JSX.Element {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const handleSave = async (): Promise<void> => {
    const payload = toPayload(form.getFieldsValue());
    if (
      payload.passDays + payload.hintCards + payload.streakProtectors === 0 ||
      payload.subscriberHintCards + payload.subscriberStreakProtectors === 0
    ) {
      setBlocked(true);
      return;
    }
    setBlocked(false);
    setSaving(true);
    try {
      await updateChestMilestone(milestone.streakDays, payload);
      message.success('Marco salvo.');
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const { streakDays, ...initial } = milestone;
  return (
    <Card title={`${streakDays} dias seguidos`} style={{ marginBottom: 16 }}>
      <Form name={`marco-${streakDays}`} form={form} layout="vertical" initialValues={initial} onValuesChange={() => setBlocked(false)}>
        <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
          <div>
            <h4>Prêmio</h4>
            <Form.Item name="passDays" label="Dias de jogos ilimitados">
              <InputNumber min={0} max={1000} precision={0} />
            </Form.Item>
            <Form.Item name="hintCards" label="Dicas">
              <InputNumber min={0} max={1000} precision={0} />
            </Form.Item>
            <Form.Item name="streakProtectors" label="Protetores">
              <InputNumber min={0} max={1000} precision={0} />
            </Form.Item>
          </div>
          <div>
            <h4>Para assinantes</h4>
            <Form.Item name="subscriberHintCards" label="Dicas">
              <InputNumber min={0} max={1000} precision={0} />
            </Form.Item>
            <Form.Item name="subscriberStreakProtectors" label="Protetores">
              <InputNumber min={0} max={1000} precision={0} />
            </Form.Item>
          </div>
        </div>
        {blocked && <Typography.Text type="danger" style={{ display: 'block', marginBottom: 8 }}>{EMPTY_ERROR}</Typography.Text>}
        <Button type="primary" loading={saving} onClick={handleSave}>
          Salvar marco
        </Button>
      </Form>
    </Card>
  );
}

export function RecompensasMarcos(): JSX.Element {
  const [milestones, setMilestones] = useState<ChestMilestone[]>([]);

  useEffect(() => {
    let cancelled = false;
    listChestMilestones()
      .then((list) => {
        if (!cancelled) setMilestones(list);
      })
      .catch(() => {
        if (!cancelled) message.error('Falha ao carregar os marcos.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      {milestones.map((m) => (
        <MilestoneCard key={m.streakDays} milestone={m} />
      ))}
    </div>
  );
}

export default RecompensasMarcos;
